// Google Analytics 4 utilities

type GtagCommand = "config" | "event" | "set";

declare global {
  interface Window {
    gtag: (
      command: GtagCommand,
      target: string,
      params?: Record<string, unknown>,
    ) => void;
  }
}

export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>,
) {
  if (typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
}

// Track CTA button clicks (request access, app store links)
export function trackCTAClick(ctaLabel: string, location: string) {
  trackEvent("cta_click", {
    cta_label: ctaLabel,
    cta_location: location,
  });
}

// Track outbound link clicks
export function trackOutboundClick(url: string, linkLabel: string) {
  trackEvent("click", {
    event_category: "outbound",
    event_label: linkLabel,
    link_url: url,
  });
}

// Track section visibility using Intersection Observer
export function observeSections() {
  const sections = document.querySelectorAll<HTMLElement>(
    ".hero, .features, .why-us, .cta",
  );

  const observed = new Set<string>();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.className.split(" ")[0] || "unknown";
          if (!observed.has(sectionName)) {
            observed.add(sectionName);
            trackEvent("section_view", { section_name: sectionName });
          }
        }
      });
    },
    { threshold: 0.5 },
  );

  sections.forEach((section) => observer.observe(section));

  return () => {
    observer.disconnect();
  };
}

// Send Web Vitals metrics to GA4
export function sendWebVitalToGA({
  name,
  delta,
  id,
}: {
  name: string;
  delta: number;
  id: string;
}) {
  trackEvent(name, {
    event_category: "Web Vitals",
    value: Math.round(name === "CLS" ? delta * 1000 : delta),
    event_label: id,
    non_interaction: true,
  });
}
